import React, { useState, useContext, useMemo, useEffect } from "react";
import _get from "lodash/get";
import ReactTooltip from "react-tooltip";

import CommentsList from "components/lib/Global/CommentsList";
import FormFieldsCard from "components/lib/Global/FormFieldsCard";
import Button from "components/lib/Shared/Button";
import { getBeneficialOwnershipFormFields } from "utils";
import BeneficialOwnershipForm from "./Form";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";

function OfficialBeneficialOwnership({
  row,
  officialBeneficialOwnershipComments,
  companyOfficialEffectKey,
  officialSliceName,
  handleSaveComment,
  beneficialOwners,
  isViewMode,
  isChangesList = false,
  shareholderPercentageChanges = {},
}) {
  const [editOfficialBeneficialOwnership, setEditOfficialBeneficialOwnership] =
    useState(false);

  const { transactions } = useContext(UpdateCompanyDetailsContext);

  // Extract transaction beneficial ownership details when in changes list
  const transactionBODetails = useMemo(() => {
    if (!isChangesList || !transactions || !Array.isArray(transactions)) {
      return null;
    }

    // Find the transaction that matches this row's transaction ID
    const matchingTransaction = transactions.find(
      (tx) => tx._id === row.original.transaction || tx._id === row.original._id
    );

    if (!matchingTransaction?.shareCapitalChanges) {
      return null;
    }

    // Find the allocation for this official
    for (const shareChange of matchingTransaction.shareCapitalChanges) {
      if (shareChange.allocations && Array.isArray(shareChange.allocations)) {
        const allocation = shareChange.allocations.find(
          (alloc) => String(alloc.officialId) === String(row.original._id)
        );

        if (allocation?.beneficialOwnershipDetails?.updatedDetails) {
          return {
            updatedDetails:
              allocation.beneficialOwnershipDetails.updatedDetails,
            currentDetails:
              allocation.beneficialOwnershipDetails.currentDetails,
            changedFields:
              allocation.beneficialOwnershipDetails.changedFields || {},
            lastUpdated: allocation.beneficialOwnershipDetails.lastUpdated,
            updatedBy: allocation.beneficialOwnershipDetails.updatedBy,
            effectiveDate: allocation.beneficialOwnershipDetails.effectiveDate,
          };
        }
      }
    }
    return null;
  }, [isChangesList, transactions, row.original._id, row.original.transaction]);

  // Create enhanced beneficial ownership form fields with transaction data and renderShortTypeOfControl
  const enhancedBeneficialOwnershipFormFields = useMemo(() => {
    const baseFields = getBeneficialOwnershipFormFields({
      beneficialOwnershipForm: row.original.beneficialOwnershipForm,
      reviewTab: true,
      beneficialOwner: row.original,
      beneficialOwners,
    });

    // If in changes list and we have transaction details, use those
    if (isChangesList && transactionBODetails) {
      // Create a beneficial ownership form object with transaction data
      const transactionBeneficialOwnershipForm = {
        ...row.original.beneficialOwnershipForm,
        ...transactionBODetails.updatedDetails,
      };

      // Create custom fields manually to bypass calculateOwnershipSummary
      const transformYesAndNo = (value) =>
        value === "yes" ? "Yes" : value === "no" ? "No" : "N/A";

      const directRightRemoveDirector = transformYesAndNo(
        transactionBeneficialOwnershipForm.directRightRemoveDirector || "N/A"
      );
      const indirectRightRemoveDirector = transformYesAndNo(
        transactionBeneficialOwnershipForm.indirectRightRemoveDirector || "N/A"
      );
      const directCompanyControlRight = transformYesAndNo(
        transactionBeneficialOwnershipForm.directCompanyControlRight || "N/A"
      );
      const indirectCompanyControlRight = transformYesAndNo(
        transactionBeneficialOwnershipForm.indirectCompanyControlRight || "N/A"
      );

      // Create fields with renderShortTypeOfControl structure
      const enhancedFields = [
        {
          key: "Percentage of shareholding",
          name: "percentShareholding",
          value: {
            direct:
              transactionBeneficialOwnershipForm.directPercentShareholding || 0,
            indirect:
              transactionBeneficialOwnershipForm.indirectPercentShareholding ||
              0,
          },
        },
        {
          key: "Percentage of voting rights",
          name: "percentVotingRights",
          value: {
            direct:
              transactionBeneficialOwnershipForm.directPercentVotingRights || 0,
            indirect:
              transactionBeneficialOwnershipForm.indirectPercentVotingRights ||
              0,
          },
        },
        {
          key: "Right to appoint/remove director",
          name: "rightRemoveDirector",
          value: {
            direct: directRightRemoveDirector,
            indirect: indirectRightRemoveDirector,
          },
        },
        {
          key: "Influence/control over the company",
          name: "companyControlRight",
          value: {
            direct: directCompanyControlRight,
            indirect: indirectCompanyControlRight,
          },
        },
      ];

      return enhancedFields;
    }

    // Check if we have percentage changes to enhance the base fields
    const currentOfficialId = row.original._id;
    if (
      shareholderPercentageChanges &&
      currentOfficialId &&
      shareholderPercentageChanges[currentOfficialId]
    ) {
      const percentageChange = shareholderPercentageChanges[currentOfficialId];

      // Find and enhance the "Percentage of shareholding" field
      const enhancedFields = baseFields.map((field) => {
        if (
          field.name === "percentShareholding" ||
          field.key === "Percentage of shareholding"
        ) {
          return {
            ...field,
            percentageChange: {
              originalPercentage: percentageChange.originalPercentage,
              newPercentage: percentageChange.newPercentage,
            },
          };
        }
        return field;
      });
      return enhancedFields;
    }

    return baseFields;
  }, [
    isChangesList,
    transactionBODetails,
    row.original,
    beneficialOwners,
    shareholderPercentageChanges,
  ]);

  // Create changed fields data for highlighting and tooltips
  const changedFields = useMemo(() => {
    if (!isChangesList || !transactionBODetails) {
      return [];
    }

    const changedFieldsData = [];
    const updatedDetails = transactionBODetails.updatedDetails;
    const currentDetails = transactionBODetails.currentDetails;

    // Map field names to their display names for renderShortTypeOfControl
    const fieldNameMap = {
      percentShareholding: "Percentage of shareholding",
      percentVotingRights: "Percentage of voting rights",
      rightRemoveDirector: "Right to Appoint/remove director",
      companyControlRight: "Influence/control over the company",
    };

    // Check each field for changes (using the short field names from renderShortTypeOfControl)
    Object.keys(fieldNameMap).forEach((fieldName) => {
      // For renderShortTypeOfControl, we need to check the underlying direct/indirect values
      let updatedValue, currentValue;

      if (fieldName === "percentShareholding") {
        updatedValue = {
          direct: updatedDetails.directPercentShareholding,
          indirect: updatedDetails.indirectPercentShareholding,
        };
        currentValue = {
          direct: currentDetails.directPercentShareholding,
          indirect: currentDetails.indirectPercentShareholding,
        };
      } else if (fieldName === "percentVotingRights") {
        updatedValue = {
          direct: updatedDetails.directPercentVotingRights,
          indirect: updatedDetails.indirectPercentVotingRights,
        };
        currentValue = {
          direct: currentDetails.directPercentVotingRights,
          indirect: currentDetails.indirectPercentVotingRights,
        };
      } else if (fieldName === "rightRemoveDirector") {
        updatedValue = {
          direct: updatedDetails.directRightRemoveDirector,
          indirect: updatedDetails.indirectRightRemoveDirector,
        };
        currentValue = {
          direct: currentDetails.directRightRemoveDirector,
          indirect: currentDetails.indirectRightRemoveDirector,
        };
      } else if (fieldName === "companyControlRight") {
        updatedValue = {
          direct: updatedDetails.directCompanyControlRight,
          indirect: updatedDetails.indirectCompanyControlRight,
        };
        currentValue = {
          direct: currentDetails.directCompanyControlRight,
          indirect: currentDetails.indirectCompanyControlRight,
        };
      }

      // Check if the field has changed by comparing the objects
      const hasChanged =
        transactionBODetails.changedFields[fieldName] ||
        JSON.stringify(updatedValue) !== JSON.stringify(currentValue);

      if (hasChanged) {
        changedFieldsData.push({
          name: fieldName,
          value: updatedValue,
          originalValue: currentValue,
          label: fieldNameMap[fieldName],
          tooltip: `Original: Direct: ${
            currentValue.direct || "N/A"
          }, Indirect: ${currentValue.indirect || "N/A"}`,
        });
      }
    });
    return changedFieldsData;
  }, [isChangesList, transactionBODetails]);

  // Initialize ReactTooltip when component mounts or changes
  useEffect(() => {
    ReactTooltip.rebuild();
  }, [changedFields]);

  if (editOfficialBeneficialOwnership) {
    return (
      <div className="px-8 pt-5 pb-6 my-6 bg-white">
        <BeneficialOwnershipForm
          row={row}
          setEditOfficialBeneficialOwnership={
            setEditOfficialBeneficialOwnership
          }
        />
      </div>
    );
  }

  // For changes list, show ownership structure without edit option
  if (isChangesList) {
    return (
      <div className="mt-4">
        <div className="mb-4">
          <h3 className="mb-4 text-md font-semibold">
            Beneficial Ownership Details
          </h3>
        </div>
        {officialBeneficialOwnershipComments &&
          officialBeneficialOwnershipComments.length > 0 && (
            <div className="px-8 pt-5 pb-6 mt-6 bg-white">
              <CommentsList
                comments={officialBeneficialOwnershipComments}
                toggleCommentStatus
              />
              <p className="mt-6 text-sm text-primary">
                Comments: {officialBeneficialOwnershipComments.length} of{" "}
                {officialBeneficialOwnershipComments.length}
              </p>
            </div>
          )}
        {/* Custom field renderer for changed fields */}
        {enhancedBeneficialOwnershipFormFields.map((field, index) => {
          const changedField = changedFields.find(
            (cf) => cf.name === field.name
          );

          if (changedField) {
            return (
              <div key={`${field.key}_${index}`} className="">
                <div className="flex items-baseline justify-between">
                  <label
                    htmlFor={field.name}
                    className="block ml-4 mt-8 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal"
                  >
                    {field.key}
                  </label>
                  <div className="bg-formInput flex w-full justify-between relative">
                    <div className="w-full relative right-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 top-[.8rem]">
                      {index === 0 && (
                        <span className="absolute -top-7">Direct</span>
                      )}
                      <p
                        data-tip={changedField.tooltip}
                        data-for={`${field.key}_direct`}
                        className="cursor-help"
                        style={{
                          backgroundColor: "#fed7aa",
                          border: "1px solid #ea580c",
                          padding: "4px 10px",
                          borderRadius: "2px",
                          position: "relative",
                          display: "inline-block",
                          minWidth: "50px",
                          textAlign: "center",
                        }}
                      >
                        {typeof field.value.direct === "number"
                          ? `${field.value.direct}%`
                          : field.value.direct}
                      </p>
                    </div>
                    {index === 0 && (
                      <div className="h-[172px] z-10 top-[85px] absolute border-l-2 border-gray-500/50 bg-formInput right-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 " />
                    )}
                    <div className="w-full relative right-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 top-[.8rem]">
                      {index === 0 && (
                        <span className="absolute -top-7">Indirect</span>
                      )}
                      <p
                        data-tip={changedField.tooltip}
                        data-for={`${field.key}_indirect`}
                        className="cursor-help"
                        style={{
                          backgroundColor: "#fed7aa",
                          border: "1px solid #ea580c",
                          padding: "4px 10px",
                          borderRadius: "2px",
                          position: "relative",
                          display: "inline-block",
                          minWidth: "50px",
                          textAlign: "center",
                        }}
                      >
                        {typeof field.value.indirect === "number"
                          ? `${field.value.indirect}%`
                          : field.value.indirect}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          return null; // Let FormFieldsCard handle non-changed fields
        })}

        {/* ReactTooltip components for changed fields */}
        {changedFields.map((changedField, index) => (
          <ReactTooltip
            key={`tooltip_direct_${index}`}
            id={`${changedField.name}_direct`}
            place="top"
            effect="solid"
            backgroundColor="#000000"
            textColor="#ffffff"
            className="custom-tooltip"
            getContent={() => changedField.tooltip}
            afterShow={() => null}
            afterHide={() => null}
          />
        ))}
        {changedFields.map((changedField, index) => (
          <ReactTooltip
            key={`tooltip_indirect_${index}`}
            id={`${changedField.name}_indirect`}
            place="top"
            effect="solid"
            backgroundColor="#000000"
            textColor="#ffffff"
            className="custom-tooltip"
            getContent={() => changedField.tooltip}
            afterShow={() => null}
            afterHide={() => null}
          />
        ))}

        {/* Custom renderer for fields with percentage changes */}
        {enhancedBeneficialOwnershipFormFields
          .filter(
            (field) => !changedFields.some((cf) => cf.name === field.name)
          )
          .map((field, index) => {
            const currentOfficialId = row.original._id;
            const hasPercentageChange =
              shareholderPercentageChanges &&
              currentOfficialId &&
              shareholderPercentageChanges[currentOfficialId] &&
              (field.name === "percentShareholding" ||
                field.key === "Percentage of shareholding");

            if (hasPercentageChange) {
              const percentageChange =
                shareholderPercentageChanges[currentOfficialId];
              const originalTotal = Number(percentageChange.originalPercentage);
              const newTotal = Number(percentageChange.newPercentage);
              const isDecrease = newTotal < originalTotal;
              const isIncrease = newTotal > originalTotal;

              return (
                <div
                  key={`${field.key}_${index}`}
                  className="flex items-baseline mt-4"
                >
                  <label
                    htmlFor={field.name}
                    className="block mb-2 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal"
                  >
                    {field.key}
                  </label>
                  <div className="bg-formInput flex w-full justify-between relative border-t-2 border-gray-300/50 pt-2">
                    <div className="w-full relative right-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 top-[.8rem]">
                      <span className="absolute -top-9">Direct</span>
                      <div className="flex flex-col space-y-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-600 font-medium">
                            Before:
                          </span>
                          <span className="bg-orange-100 border border-orange-300 text-orange-800 px-2 py-1 rounded text-xs">
                            {typeof field.value.direct === "number"
                              ? `${field.value.direct}%`
                              : field.value.direct}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-600 font-medium">
                            Now:
                          </span>
                          <span
                            className={`px-2 py-1 rounded text-xs font-bold ${
                              isIncrease
                                ? "bg-green-100 border border-green-300 text-green-800"
                                : isDecrease
                                ? "bg-red-100 border border-red-300 text-red-800"
                                : "bg-gray-100 border border-gray-300 text-gray-800"
                            }`}
                          >
                            {typeof field.value.direct === "number"
                              ? `${field.value.direct}%`
                              : field.value.direct}
                          </span>
                          {isIncrease && (
                            <span className="text-green-600 text-xs font-bold">
                              ↗
                            </span>
                          )}
                          {isDecrease && (
                            <span className="text-red-600 text-xs font-bold">
                              ↘
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="h-[172px] z-10 top-[85px] absolute border-l-2 border-gray-500/50 bg-formInput right-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                    <div className="w-full relative right-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 top-[.8rem]">
                      <span className="absolute -top-9">Indirect</span>
                      <div className="flex flex-col space-y-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-600 font-medium">
                            Before:
                          </span>
                          <span className="bg-orange-100 border border-orange-300 text-orange-800 px-2 py-1 rounded text-xs">
                            {typeof field.value.indirect === "number"
                              ? `${field.value.indirect}%`
                              : field.value.indirect}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-600 font-medium">
                            Now:
                          </span>
                          <span
                            className={`px-2 py-1 rounded text-xs font-bold ${
                              isIncrease
                                ? "bg-green-100 border border-green-300 text-green-800"
                                : isDecrease
                                ? "bg-red-100 border border-red-300 text-red-800"
                                : "bg-gray-100 border border-gray-300 text-gray-800"
                            }`}
                          >
                            {typeof field.value.indirect === "number"
                              ? `${field.value.indirect}%`
                              : field.value.indirect}
                          </span>
                          {isIncrease && (
                            <span className="text-green-600 text-xs font-bold">
                              ↗
                            </span>
                          )}
                          {isDecrease && (
                            <span className="text-red-600 text-xs font-bold">
                              ↘
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return null; // Let FormFieldsCard handle regular fields
          })}

        <FormFieldsCard
          handleSaveComment={handleSaveComment}
          fields={enhancedBeneficialOwnershipFormFields.filter((field) => {
            // Filter out changed fields and fields with percentage changes that we render custom
            const isChanged = changedFields.some(
              (cf) => cf.name === field.name
            );
            const currentOfficialId = row.original._id;
            const hasPercentageChange =
              shareholderPercentageChanges &&
              currentOfficialId &&
              shareholderPercentageChanges[currentOfficialId] &&
              (field.name === "percentShareholding" ||
                field.key === "Percentage of shareholding");
            return !isChanged && !hasPercentageChange;
          })}
          resolvedFields={_get(
            row.original.beneficialOwnershipForm,
            "resolvedFields"
          )}
          addReviewComment={!isViewMode}
          formName="beneficialOwnershipForm"
          formHelperKey="beneficialOwnershipForm"
          updateEffectKey={companyOfficialEffectKey}
          updateEffectPath={`${officialSliceName}.beneficialOwnershipForm`}
          formId={_get(row.original.beneficialOwnershipForm, "_id")}
          comments={officialBeneficialOwnershipComments}
          hideResolveAllCheckbox={isViewMode}
        />
      </div>
    );
  }

  return (
    <div className="mt-4">
      <Button
        className="mb-2"
        onClick={() => {
          setEditOfficialBeneficialOwnership(true);
        }}
      >
        Edit ownership
      </Button>
      {officialBeneficialOwnershipComments &&
        officialBeneficialOwnershipComments.length > 0 && (
          <div className="px-8 pt-5 pb-6 mt-6 bg-white">
            <CommentsList
              comments={officialBeneficialOwnershipComments}
              toggleCommentStatus
            />
            <p className="mt-6 text-sm text-primary">
              Comments: {officialBeneficialOwnershipComments.length} of{" "}
              {officialBeneficialOwnershipComments.length}
            </p>
          </div>
        )}
      <FormFieldsCard
        handleSaveComment={handleSaveComment}
        fields={getBeneficialOwnershipFormFields({
          beneficialOwnershipForm: row.original.beneficialOwnershipForm,
          reviewTab: true,
          beneficialOwner: row.original,
          beneficialOwners,
        })}
        resolvedFields={_get(
          row.original.beneficialOwnershipForm,
          "resolvedFields"
        )}
        addReviewComment={!isViewMode}
        formName="beneficialOwnershipForm"
        formHelperKey="beneficialOwnershipForm"
        updateEffectKey={companyOfficialEffectKey}
        updateEffectPath={`${officialSliceName}.beneficialOwnershipForm`}
        formId={_get(row.original.beneficialOwnershipForm, "_id")}
        comments={officialBeneficialOwnershipComments}
        hideResolveAllCheckbox={isViewMode}
      />
    </div>
  );
}

export default OfficialBeneficialOwnership;
